import type { FC } from 'react';

import type { Link as CmsLink } from '@eneco/cms-types';
import { linkWithUrl } from '@eneco/cms-types';
import type { ContentListItemContent } from '@eneco/cms-types/generated/ContentListItem';
import RichText from '@eneco/components/RichText/RichText';
import { Checklist } from '@eneco/components/ui/Checklist/Checklist';
import BrandBox from '@eneco/custom-components/BrandBox/BrandBox';
import { useFormatter } from '@eneco/i18n';
import { useLinkComponent } from '@eneco/link';
import { Stack, ButtonLink, Grid, Card, Box, Text, Stretch, Bleed } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';

/**
 * A usp's text is `value` on Sanity. On Sitecore it sits in the item's `Content` section, whose one field arrives as
 * `{ value: { value } }`: the adapter takes that for a field and hands it over as `content` instead.
 */
type Usp = ContentListItemContent & { content?: string; _key?: string };

type Props = {
  title?: string;
  buttonLink?: CmsLink;
  uspTitle?: string;
  uspList?: Usp[];
  costSavings: string;
};

type ContentBlockProps = Pick<Props, 'uspList' | 'buttonLink' | 'uspTitle'> & {
  Link: ReturnType<typeof useLinkComponent>;
};

const ContentBlock: FC<ContentBlockProps> = ({ uspTitle, buttonLink, uspList, Link }) => {
  const link = linkWithUrl(buttonLink);
  // A reference the migration could not resolve has no text and is left out rather than rendered as an empty row. A usp
  // added in the Studio has text but no Sitecore item name, so it is keyed by its `_key`.
  const usps = uspList?.filter(usp => usp.value ?? usp.content);

  return (
    <Box borderRadius="m" padding={{ initial: '0', lg: '8' }} paddingTop={{ initial: '6', lg: '12' }}>
      <Stack gap="6">
        {uspTitle && (
          <Text size={{ initial: 'BodyM', lg: 'BodyL' }}>
            <RichText html={uspTitle} />
          </Text>
        )}

        {usps?.length && (
          <Checklist>
            {usps.map(usp => {
              return (
                <Checklist.Item alignY="start" key={usp.itemName ?? usp._key}>
                  {usp.value ?? usp.content}
                </Checklist.Item>
              );
            })}
          </Checklist>
        )}
        {link?.text && (
          <Stack direction="row" gap="3">
            <Stack.Item grow={false}>
              <Stretch width={{ initial: true, lg: false }}>
                <Link linkType={link.linkType ?? ''} linkValue={{ href: link.href, target: link.target }}>
                  <ButtonLink target={link.target}>{link.text}</ButtonLink>
                </Link>
              </Stretch>
            </Stack.Item>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const HeroCard = ({ title, buttonLink, uspList, uspTitle, costSavings }: Props) => {
  const Link = useLinkComponent();
  const { currency } = useFormatter();

  const isDesktop = useMediaQuery('lg');

  const costSavingsFormatted = currency.euroNoFractionDigits(costSavings);
  const costSavingsFromFormatted = currency.euroNoFractionDigits(+costSavings * 0.9);

  return (
    <Grid columns={{ initial: '1', lg: '12' }}>
      <Grid.Item gridColumn={{ initial: '1/-1', lg: '1/11' }} gridRow={{ lg: '1 / -1' }} order={{ lg: 2 }}>
        <BrandBox
          title={`${title ?? ''} ${costSavingsFromFormatted} - ${costSavingsFormatted}`}
          borderRadius="all"
          padding={{ initial: '6', lg: '8' }}
          size={{ initial: 'M', lg: 'L' }}
        />
      </Grid.Item>

      <Grid.Item gridColumn={{ initial: '1/-1', lg: '1/13' }}>
        <Bleed top={{ initial: '0', lg: '6' }}>
          <Box paddingLeft={{ initial: '0', lg: '8' }}>
            {isDesktop ? (
              <Card corners="rounded" overflow="hidden">
                <ContentBlock uspTitle={uspTitle} buttonLink={buttonLink} uspList={uspList} Link={Link} />
              </Card>
            ) : (
              <ContentBlock uspTitle={uspTitle} buttonLink={buttonLink} uspList={uspList} Link={Link} />
            )}
          </Box>
        </Bleed>
      </Grid.Item>
    </Grid>
  );
};
export default HeroCard;
